/**
 * HistogramPanel — live band statistics for the active raster.
 *
 * Fetches TiTiler `/cog/statistics` for the selected layer's COG and renders
 * min / max / mean / std plus an SVG histogram from the response bins.
 * No charting library dependency — the bars are plain SVG.
 *
 * When no layer is selected or the request fails, the panel keeps a clear
 * empty / error state so the ToolCard never looks broken.
 */

import { useEffect, useMemo, useState } from 'react';
import { Callout, Chip, tokens } from '@/design';
import { useViewerSlice } from '@/state/useViewerSlice';
import { extractCogQuery } from '@/utils/titilerRasterProfile';

export interface HistogramPanelProps {
  forceVisible?: boolean;
  /**
   * When false the panel stays idle and does not hit `/cog/statistics`.
   * Used so opening a raster layer does not pay for histogram work until
   * the operator expands that section.
   */
  enabled?: boolean;
  'data-testid'?: string;
}

type BandStats = {
  readonly name: string;
  readonly min: number;
  readonly max: number;
  readonly mean: number;
  readonly std: number;
  readonly percentile2?: number;
  readonly percentile98?: number;
  readonly counts: readonly number[];
  readonly bins: readonly number[];
};

type LoadState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; bands: readonly BandStats[] }
  | { status: 'error'; message: string };

function readNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function parseBandStats(payload: Record<string, unknown>): BandStats[] {
  const bands: BandStats[] = [];
  for (const [name, raw] of Object.entries(payload)) {
    if (!raw || typeof raw !== 'object') continue;
    const entry = raw as Record<string, unknown>;
    const min = readNumber(entry.min);
    const max = readNumber(entry.max);
    const mean = readNumber(entry.mean);
    const std = readNumber(entry.std);
    if (min === null || max === null || mean === null || std === null) continue;
    const histogram = entry.histogram;
    let counts: number[] = [];
    let bins: number[] = [];
    if (Array.isArray(histogram) && histogram.length >= 2) {
      counts = Array.isArray(histogram[0])
        ? (histogram[0] as unknown[]).map((v) => Number(v)).filter(Number.isFinite)
        : [];
      bins = Array.isArray(histogram[1])
        ? (histogram[1] as unknown[]).map((v) => Number(v)).filter(Number.isFinite)
        : [];
    }
    bands.push({
      name,
      min,
      max,
      mean,
      std,
      percentile2: readNumber(entry.percentile_2) ?? undefined,
      percentile98: readNumber(entry.percentile_98) ?? undefined,
      counts,
      bins,
    });
  }
  return bands;
}

function formatStat(value: number): string {
  if (Math.abs(value) >= 1000) return value.toFixed(1);
  if (Math.abs(value) >= 1) return value.toFixed(2);
  return value.toPrecision(3);
}

function HistogramSvg({ counts }: { counts: readonly number[] }) {
  if (counts.length === 0) return null;
  const peak = Math.max(...counts, 1);
  const width = 240;
  const height = 64;
  const gap = 1;
  const barWidth = Math.max(1, (width - gap * (counts.length - 1)) / counts.length);
  return (
    <svg
      width="100%"
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label="Band histogram"
      style={{ display: 'block' }}
    >
      {counts.map((count, index) => {
        const barHeight = (count / peak) * (height - 2);
        const x = index * (barWidth + gap);
        const y = height - barHeight;
        return (
          <rect
            key={index}
            x={x}
            y={y}
            width={barWidth}
            height={barHeight}
            fill={tokens.color.accent.primary}
            opacity={0.85}
          />
        );
      })}
    </svg>
  );
}

export function HistogramPanel({
  enabled = true,
  'data-testid': testId,
}: HistogramPanelProps) {
  const sceneSelection = useViewerSlice((s) => s.sceneSelection);
  const catalog = useViewerSlice((s) => s.catalog);
  const [loadState, setLoadState] = useState<LoadState>({ status: 'idle' });

  const tilesUrl = useMemo(() => {
    const id = sceneSelection.layerId ?? sceneSelection.sceneItemId;
    if (!id) return null;
    const layer = catalog.find((candidate) => candidate.id === id);
    const tiles = layer?.endpoints?.tiles ?? layer?.endpoints?.terrain;
    return typeof tiles === 'string' && tiles.includes('/cog/') ? tiles : null;
  }, [catalog, sceneSelection.layerId, sceneSelection.sceneItemId]);

  useEffect(() => {
    if (!enabled) {
      setLoadState({ status: 'idle' });
      return;
    }
    if (!tilesUrl) {
      setLoadState({ status: 'idle' });
      return;
    }
    const parts = extractCogQuery(tilesUrl);
    if (!parts) {
      setLoadState({ status: 'error', message: 'Not a TiTiler COG template' });
      return;
    }
    let cancelled = false;
    setLoadState({ status: 'loading' });
    const url = `${parts.base}/cog/statistics?url=${parts.cogParam}&max_size=256&histogram_bins=32`;
    void (async () => {
      try {
        const res = await fetch(url, { signal: AbortSignal.timeout(12000) });
        if (cancelled) return;
        if (!res.ok) {
          setLoadState({ status: 'error', message: `HTTP ${res.status}` });
          return;
        }
        const json = (await res.json()) as Record<string, unknown>;
        const bands = parseBandStats(json);
        if (bands.length === 0) {
          setLoadState({ status: 'error', message: 'No band statistics returned' });
          return;
        }
        setLoadState({ status: 'ready', bands });
      } catch (err) {
        if (cancelled) return;
        setLoadState({
          status: 'error',
          message: err instanceof Error ? err.message : 'request failed',
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, tilesUrl]);

  return (
    <div
      data-testid={testId ?? 'raster-histogram-panel'}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.space[2],
      }}
    >
      {!enabled || loadState.status === 'idle' ? (
        <Callout severity="info" icon="info" title="Histogram">
          {enabled
            ? 'Select a COG raster to load band statistics.'
            : 'Expand this section to load band statistics.'}
        </Callout>
      ) : null}
      {loadState.status === 'loading' ? (
        <Chip icon="pending" variant="info" label="Loading statistics…" />
      ) : null}
      {loadState.status === 'error' ? (
        <Callout severity="warning" icon="warning" title="Statistics unavailable">
          {loadState.message}
        </Callout>
      ) : null}
      {loadState.status === 'ready'
        ? loadState.bands.map((band) => (
            <div
              key={band.name}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: tokens.space[2],
                padding: tokens.space[2],
                border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
                borderRadius: tokens.radius.md,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: tokens.space[1],
                }}
              >
                <Chip icon="query_stats" variant="neutral" label={band.name} />
                <Chip variant="neutral" label={`min ${formatStat(band.min)}`} />
                <Chip variant="neutral" label={`max ${formatStat(band.max)}`} />
                <Chip variant="neutral" label={`μ ${formatStat(band.mean)}`} />
                <Chip variant="neutral" label={`σ ${formatStat(band.std)}`} />
                {band.percentile2 != null && band.percentile98 != null ? (
                  <Chip
                    variant="neutral"
                    label={`p2–p98 ${formatStat(band.percentile2)}…${formatStat(band.percentile98)}`}
                  />
                ) : null}
              </div>
              <HistogramSvg counts={band.counts} />
            </div>
          ))
        : null}
    </div>
  );
}
